
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, DollarSign, ChevronUp, ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { VisaGuideData } from "@/types/visa";

interface VisaProcessProps {
  visaData: VisaGuideData[string][string];
  expandedSteps: number[];
  onToggleStep: (index: number) => void;
}

const VisaProcess = ({ visaData, expandedSteps, onToggleStep }: VisaProcessProps) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl">{visaData.process.title}</CardTitle>
        <CardDescription>{visaData.process.description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-600 mb-1">Estimated Timeframe</h4>
            <div className="flex items-center">
              <Clock className="h-5 w-5 text-gray-700 mr-2" />
              <span className="font-semibold">{visaData.process.timeframe}</span>
            </div>
          </div>
          <div className="flex-1 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-600 mb-1">Application Cost</h4>
            <div className="flex items-center">
              <DollarSign className="h-5 w-5 text-gray-700 mr-2" />
              <span className="font-semibold">{visaData.process.cost}</span>
            </div>
          </div>
        </div>
        
        <h3 className="font-semibold text-lg mb-4">Application Steps:</h3>
        <div className="space-y-4">
          {visaData.process.steps.map((step, index) => (
            <div key={index} className="relative">
              <div className="flex">
                <div className="relative flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-800 font-bold text-lg mr-4 z-10">
                  {index + 1}
                  {index < visaData.process.steps.length - 1 && (
                    <div className="absolute top-10 left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gray-200" />
                  )}
                </div>
                <Collapsible 
                  className="flex-1" 
                  open={expandedSteps.includes(index)} 
                  onOpenChange={() => onToggleStep(index)}
                >
                  <CollapsibleTrigger className="flex items-center justify-between w-full p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="text-left">
                      <h4 className="font-medium">{step.title}</h4>
                      <p className="text-sm text-gray-500">{step.description}</p>
                    </div>
                    {expandedSteps.includes(index) ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </CollapsibleTrigger>
                  {step.details && (
                    <CollapsibleContent className="mt-2 p-4 border border-t-0 rounded-b-lg bg-gray-50">
                      <p>{step.details}</p>
                    </CollapsibleContent>
                  )}
                </Collapsible>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default VisaProcess;
